import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Settings, Layers, Calendar } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { api, setAccessToken } from "@/api";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const AgencyOrganizationsPage = () => {
  const { token, setStaffOrgAccess } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  const [stampCardsTogglingOrgId, setStampCardsTogglingOrgId] = useState(null);
  const [stampCardsConfirm, setStampCardsConfirm] = useState({
    open: false,
    orgId: null,
    orgName: "",
    pricing: null,
  });
  const [stampCardsConfirmLoading, setStampCardsConfirmLoading] = useState(false);

  const [shoreTogglingOrgId, setShoreTogglingOrgId] = useState(null);
  const [shoreConfirm, setShoreConfirm] = useState({
    open: false,
    orgId: null,
    orgName: "",
    pricing: null,
  });
  const [shoreConfirmLoading, setShoreConfirmLoading] = useState(false);

  const [integrationsModalOrg, setIntegrationsModalOrg] = useState(null);

  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);

  const [accessLogPage, setAccessLogPage] = useState(1);
  const [accessLogItems, setAccessLogItems] = useState([]);
  const [accessLogTotal, setAccessLogTotal] = useState(0);
  const [accessLogPages, setAccessLogPages] = useState(1);
  const [accessLogItemsLoading, setAccessLogItemsLoading] = useState(false);

  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgOwnerEmail, setNewOrgOwnerEmail] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);

  const [testQrOpen, setTestQrOpen] = useState(false);
  const [testQrOrg, setTestQrOrg] = useState(null);
  const [testQrCampaigns, setTestQrCampaigns] = useState([]);
  const [testQrActions, setTestQrActions] = useState([]);
  const [testQrCampaignId, setTestQrCampaignId] = useState("");
  const [testQrActionId, setTestQrActionId] = useState("");
  const [testQrLoadingCampaigns, setTestQrLoadingCampaigns] = useState(false);
  const [testQrLoadingActions, setTestQrLoadingActions] = useState(false);
  const [testQrGenerating, setTestQrGenerating] = useState(false);
  const [testQrResult, setTestQrResult] = useState(null); // { code, redeemUrl, dataUrl, reused }

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setOrgLoading(true);
        const items = await api.agencyOrganizations(token);
        setOrganizations(Array.isArray(items) ? items : []);
      } catch (_) {
        setOrganizations([]);
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setAccessLogsLoading(true);
        const resp = await api.agencyAccessLogs(token, { days: 365, limit: 1000, page: 1 });
        setAccessLogs(Array.isArray(resp?.items) ? resp.items : []);
      } catch (_) {
        setAccessLogs([]);
      } finally {
        setAccessLogsLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setAccessLogItemsLoading(true);
        const resp = await api.agencyAccessLogs(token, { days: 60, limit: 5, page: accessLogPage });
        setAccessLogItems(Array.isArray(resp?.items) ? resp.items : []);
        setAccessLogTotal(Number(resp?.total || 0));
        setAccessLogPages(Number(resp?.pages || 1));
      } catch (_) {
        setAccessLogItems([]);
        setAccessLogTotal(0);
        setAccessLogPages(1);
      } finally {
        setAccessLogItemsLoading(false);
      }
    })();
  }, [token, accessLogPage]);

  useEffect(() => {
    if (accessLogPage > accessLogPages) setAccessLogPage(accessLogPages);
  }, [accessLogPage, accessLogPages]);

  const lastSwitchByCompanyId = useMemo(() => {
    const map = new Map();
    for (const entry of accessLogs) {
      const cid = entry?.company?.id;
      if (!cid) continue;
      if (!map.has(cid)) map.set(cid, entry);
    }
    return map;
  }, [accessLogs]);

  const handleSwitchOrganization = async (companyId) => {
    try {
      setSwitchingOrgId(companyId);
      const res = await api.agencySwitchOrganization(token, companyId);

      setAccessToken(res.accessToken);
      setStaffOrgAccess(res.accessToken, res.user, res.company);

      toast({
        title: t("agency.organizations.switched"),
        description: t("agency.organizations.switchedTo", { name: res.company.name }),
      });

      await new Promise((resolve) => setTimeout(resolve, 100));
      navigate("/merchant");
    } catch (err) {
      toast({
        title: t("agency.organizations.switchError"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      setCreatingOrg(true);
      const resp = await api.agencyCreateOrganization(token, {
        name: newOrgName,
        ownerEmail: newOrgOwnerEmail,
      });

      if (resp?.inviteUrl && resp?.licenseKey) {
        toast({
          title: t("agency.organizations.created"),
          description: `Invite-Link: ${resp.inviteUrl}\nLizenz: ${resp.licenseKey}`,
        });
      } else if (resp?.inviteUrl) {
        toast({
          title: t("agency.organizations.created"),
          description: `Invite-Link: ${resp.inviteUrl}`,
        });
      } else {
        toast({
          title: t("agency.organizations.created"),
          description: t("agency.organizations.inviteSent"),
        });
      }

      setNewOrgName("");
      setNewOrgOwnerEmail("");

      const items = await api.agencyOrganizations(token);
      setOrganizations(Array.isArray(items) ? items : []);
    } catch (err) {
      toast({
        title: t("agency.organizations.createError"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setCreatingOrg(false);
    }
  };

  const formatDateTime = (d) => {
    try {
      return new Date(d).toLocaleString("de-DE");
    } catch (_) {
      return "";
    }
  };

  const applyOrgPatch = (orgId, patch) => {
    setOrganizations((prev) =>
      (prev || []).map((o) => (o.id === orgId ? { ...o, ...(patch || {}) } : o))
    );
  };

  const toggleStampCards = async ({ orgId, orgName, enable, confirmed = false }) => {
    if (!token || !orgId) return;
    try {
      setStampCardsTogglingOrgId(orgId);

      const resp = await api.agencyToggleStampCards(token, orgId, enable, confirmed);

      if (resp?.requiresConfirmation) {
        setStampCardsConfirm({
          open: true,
          orgId,
          orgName: orgName || "",
          pricing: resp?.pricing || null,
        });
        return;
      }

      if (resp?.ok) {
        applyOrgPatch(orgId, { stampCardsEnabled: !!resp.stampCardsEnabled });
        toast({ title: resp?.message || (enable ? "Stempelkarten aktiviert" : "Stempelkarten deaktiviert") });
      } else {
        throw new Error(resp?.error || resp?.message || "Unbekannter Fehler");
      }
    } catch (err) {
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setStampCardsTogglingOrgId(null);
    }
  };

  const toggleShore = async ({ orgId, orgName, enable, confirmed = false }) => {
    if (!token || !orgId) return;
    try {
      setShoreTogglingOrgId(orgId);
      const resp = await api.agencyToggleShore(token, orgId, enable, confirmed);

      if (resp?.requiresConfirmation) {
        setShoreConfirm({
          open: true,
          orgId,
          orgName: orgName || "",
          pricing: resp?.pricing || null,
        });
        return;
      }

      if (resp?.ok) {
        applyOrgPatch(orgId, { shoreEnabled: !!resp.shoreEnabled });
        toast({ title: resp?.message || (enable ? "Shore aktiviert" : "Shore deaktiviert") });
      } else {
        throw new Error(resp?.error || resp?.message || "Unbekannter Fehler");
      }
    } catch (err) {
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setShoreTogglingOrgId(null);
    }
  };

  const openTestQr = async (org) => {
    if (!token) return;
    setTestQrOrg(org);
    setTestQrOpen(true);
    setTestQrCampaigns([]);
    setTestQrActions([]);
    setTestQrCampaignId("");
    setTestQrActionId("");
    setTestQrResult(null);

    try {
      setTestQrLoadingCampaigns(true);
      const resp = await api.agencyTestQrCampaigns(token, org.id);
      setTestQrCampaigns(Array.isArray(resp?.campaigns) ? resp.campaigns : []);
    } catch (err) {
      setTestQrCampaigns([]);
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setTestQrLoadingCampaigns(false);
    }
  };

  const loadTestQrActions = async (orgId, campaignId) => {
    if (!token) return;
    try {
      setTestQrLoadingActions(true);
      const resp = await api.agencyTestQrActions(token, orgId, campaignId);
      setTestQrActions(Array.isArray(resp?.actions) ? resp.actions : []);
    } catch (err) {
      setTestQrActions([]);
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setTestQrLoadingActions(false);
    }
  };

  const generateTestQr = async () => {
    if (!token || !testQrOrg?.id || !testQrActionId) return;
    try {
      setTestQrGenerating(true);
      const resp = await api.agencyCreateTestQr(token, testQrOrg.id, testQrActionId);
      const code = resp?.code;
      const reused = !!resp?.reused;
      if (!code) throw new Error("No code returned");
      const redeemUrl = `${window.location.origin}/redeem/${encodeURIComponent(code)}`;
      const dataUrl = await QRCode.toDataURL(redeemUrl, { margin: 1, scale: 6, errorCorrectionLevel: "M" });
      setTestQrResult({ code, redeemUrl, dataUrl, reused });
    } catch (err) {
      setTestQrResult(null);
      toast({
        title: t("common.error"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setTestQrGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-xl font-semibold text-foreground">{t("agency.pages.organizationsTitle")}</h1>

        {orgLoading ? (
          <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
        ) : organizations.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("agency.organizations.empty")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {organizations.map((o) => {
              const last = lastSwitchByCompanyId.get(o.id);
              const canOpen = !!o.allowRemoteAccess;
              const stampEnabled = !!o.stampCardsEnabled;
              const shoreIntegEnabled = !!o.shoreEnabled;
              return (
                <div key={o.id} className="bg-background p-4 rounded shadow text-sm space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-foreground truncate">{o.name}</div>
                      <div className="text-xs text-muted-foreground">{t("agency.cards.status")}: {o.status}</div>
                      <div className="text-xs text-muted-foreground">
                        {canOpen ? t("agency.organizations.remoteOn") : t("agency.organizations.remoteOff")}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {stampEnabled && (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                            Stempelkarten aktiv
                          </span>
                        )}
                        {shoreIntegEnabled && (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                            Shore aktiv
                          </span>
                        )}
                        {!stampEnabled && !shoreIntegEnabled && (
                          <span className="text-[10px] text-muted-foreground">Keine Integrationen aktiv</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {t("agency.cards.createdAt")}: {new Date(o.createdAt).toLocaleDateString("de-DE")}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {t("agency.cards.lastSwitch")}: {last ? `${last.user?.email || ""} · ${formatDateTime(last.createdAt)}` : t("agency.cards.lastSwitchNone")}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs"
                      onClick={() => setIntegrationsModalOrg(o)}
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Integrationen
                    </button>

                    {canOpen && (
                      <>
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs disabled:opacity-60"
                          disabled={switchingOrgId === o.id}
                          onClick={() => handleSwitchOrganization(o.id)}
                        >
                          {switchingOrgId === o.id
                            ? t("agency.organizations.switching")
                            : t("agency.organizations.openDashboard")}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1.5 rounded border text-xs"
                          onClick={() => openTestQr(o)}
                        >
                          {t("agency.organizations.testQrButton")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Integrations Modal */}
        <Dialog
          open={!!integrationsModalOrg}
          onOpenChange={(v) => { if (!v) setIntegrationsModalOrg(null); }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Integrationen</DialogTitle>
            </DialogHeader>
            {integrationsModalOrg && (() => {
              const o = organizations.find((x) => x.id === integrationsModalOrg.id) || integrationsModalOrg;
              const sEnabled = !!o.stampCardsEnabled;
              const shEnabled = !!o.shoreEnabled;
              const stampBusy = stampCardsTogglingOrgId === o.id;
              const shoreBusy = shoreTogglingOrgId === o.id;
              return (
                <div className="space-y-3 py-1">
                  <p className="text-xs text-muted-foreground">
                    Integrationen für <strong>{o.name}</strong> aktivieren oder deaktivieren.
                  </p>

                  {/* Stempelkarten */}
                  <div className="flex items-center justify-between border rounded-lg px-4 py-3">
                    <div>
                      <div className="text-sm font-medium"><Layers className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />Stempelkarten</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {sEnabled
                          ? <span className="text-emerald-600">Aktiv</span>
                          : <span className="text-muted-foreground">Inaktiv</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={stampBusy || stampCardsConfirmLoading}
                      onClick={async () => {
                        await toggleStampCards({ orgId: o.id, orgName: o.name, enable: !sEnabled });
                      }}
                      className={[
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50",
                        sEnabled ? "bg-emerald-500" : "bg-gray-200",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                          sEnabled ? "translate-x-6" : "translate-x-1",
                        ].join(" ")}
                      />
                    </button>
                  </div>

                  {/* Shore */}
                  <div className="flex items-center justify-between border rounded-lg px-4 py-3">
                    <div>
                      <div className="text-sm font-medium"><Calendar className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />Shore Terminbuchung</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {shEnabled
                          ? <span className="text-emerald-600">Aktiv</span>
                          : <span className="text-muted-foreground">Inaktiv</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={shoreBusy || shoreConfirmLoading}
                      onClick={async () => {
                        await toggleShore({ orgId: o.id, orgName: o.name, enable: !shEnabled });
                      }}
                      className={[
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50",
                        shEnabled ? "bg-emerald-500" : "bg-gray-200",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                          shEnabled ? "translate-x-6" : "translate-x-1",
                        ].join(" ")}
                      />
                    </button>
                  </div>
                </div>
              );
            })()}
            <DialogFooter>
              <button
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded border text-xs"
                onClick={() => setIntegrationsModalOrg(null)}
              >
                Schließen
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={stampCardsConfirm.open}
          onOpenChange={(open) => {
            if (!open && stampCardsConfirmLoading) return;
            if (!open) setStampCardsConfirm({ open: false, orgId: null, orgName: "", pricing: null });
            else setStampCardsConfirm((s) => ({ ...s, open }));
          }}
          type="info"
          title="Stempelkarten aktivieren?"
          description={
            stampCardsConfirm?.pricing
              ? `Für "${stampCardsConfirm.orgName}" wird StempelMirEin aktiviert. Kosten: ${stampCardsConfirm.pricing.monthlyPrice}€/Monat. ${stampCardsConfirm.pricing.description || ""}`
              : `Für "${stampCardsConfirm.orgName}" wird StempelMirEin aktiviert. Fortfahren?`
          }
          confirmText="Aktivieren"
          cancelText={t("common.cancel")}
          loading={stampCardsConfirmLoading}
          onCancel={() => setStampCardsConfirm({ open: false, orgId: null, orgName: "", pricing: null })}
          onConfirm={async () => {
            if (!stampCardsConfirm?.orgId) return;
            try {
              setStampCardsConfirmLoading(true);
              const orgId = stampCardsConfirm.orgId;
              const orgName = stampCardsConfirm.orgName;
              setStampCardsConfirm({ open: false, orgId: null, orgName: "", pricing: null });
              await toggleStampCards({ orgId, orgName, enable: true, confirmed: true });
            } finally {
              setStampCardsConfirmLoading(false);
            }
          }}
        />

        <ConfirmDialog
          open={shoreConfirm.open}
          onOpenChange={(open) => {
            if (!open && shoreConfirmLoading) return;
            if (!open) setShoreConfirm({ open: false, orgId: null, orgName: "", pricing: null });
            else setShoreConfirm((s) => ({ ...s, open }));
          }}
          type="info"
          title="Shore-Integration aktivieren?"
          description={
            shoreConfirm?.pricing
              ? `Für "${shoreConfirm.orgName}" wird die Shore-Integration aktiviert. Kosten: ${shoreConfirm.pricing.monthlyPrice}€/Monat. ${shoreConfirm.pricing.description || ""}`
              : `Für "${shoreConfirm.orgName}" wird die Shore-Integration aktiviert. Fortfahren?`
          }
          confirmText="Aktivieren"
          cancelText={t("common.cancel")}
          loading={shoreConfirmLoading}
          onCancel={() => setShoreConfirm({ open: false, orgId: null, orgName: "", pricing: null })}
          onConfirm={async () => {
            if (!shoreConfirm?.orgId) return;
            try {
              setShoreConfirmLoading(true);
              const orgId = shoreConfirm.orgId;
              const orgName = shoreConfirm.orgName;
              setShoreConfirm({ open: false, orgId: null, orgName: "", pricing: null });
              await toggleShore({ orgId, orgName, enable: true, confirmed: true });
            } finally {
              setShoreConfirmLoading(false);
            }
          }}
        />

        <Dialog
          open={testQrOpen}
          onOpenChange={(v) => {
            if (!v && testQrGenerating) return;
            setTestQrOpen(v);
            if (!v) {
              setTestQrOrg(null);
              setTestQrCampaigns([]);
              setTestQrActions([]);
              setTestQrCampaignId("");
              setTestQrActionId("");
              setTestQrResult(null);
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("agency.organizations.testQrTitle")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 text-xs">
              <div className="text-[11px] text-muted-foreground">
                {t("agency.organizations.testQrDesc")}
                {testQrOrg?.name ? <div className="mt-1 text-foreground"><strong>{testQrOrg.name}</strong></div> : null}
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-muted-foreground">{t("agency.organizations.testQrCampaign")}</label>
                <select
                  className="border px-2 py-1 rounded text-xs w-full"
                  value={testQrCampaignId}
                  disabled={testQrLoadingCampaigns}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTestQrCampaignId(v);
                    setTestQrActionId("");
                    setTestQrActions([]);
                    setTestQrResult(null);
                    if (v && testQrOrg?.id) loadTestQrActions(testQrOrg.id, v);
                  }}
                >
                  <option value="">{testQrLoadingCampaigns ? t("common.loading") : "—"}</option>
                  {testQrCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.active === false ? " (inaktiv)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-muted-foreground">{t("agency.organizations.testQrAction")}</label>
                <select
                  className="border px-2 py-1 rounded text-xs w-full"
                  value={testQrActionId}
                  disabled={!testQrCampaignId || testQrLoadingActions}
                  onChange={(e) => {
                    setTestQrActionId(e.target.value);
                    setTestQrResult(null);
                  }}
                >
                  <option value="">{testQrLoadingActions ? t("common.loading") : "—"}</option>
                  {testQrActions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.title}{a.active === false ? " (inaktiv)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs disabled:opacity-60"
                disabled={!testQrActionId || testQrGenerating}
                onClick={generateTestQr}
              >
                {testQrGenerating ? t("agency.organizations.testQrGenerating") : t("agency.organizations.testQrGenerate")}
              </button>

              {testQrResult && (
                <div className="border rounded p-3 bg-muted space-y-2">
                  <div className="text-[11px] text-muted-foreground">
                    {testQrResult.reused ? t("agency.organizations.testQrReused") : t("agency.organizations.testQrCreated")}
                  </div>
                  <div className="flex items-start gap-3">
                    <img src={testQrResult.dataUrl} alt="Test QR" className="w-32 h-32 bg-background border rounded p-2" />
                    <div className="min-w-0 space-y-2">
                      <div>
                        <div className="text-[11px] text-muted-foreground">{t("agency.organizations.testQrCodeLabel")}</div>
                        <div className="font-mono text-xs break-all">{testQrResult.code}</div>
                      </div>
                      <div>
                        <div className="text-[11px] text-muted-foreground">URL</div>
                        <div className="font-mono text-[11px] break-all">{testQrResult.redeemUrl}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center px-2 py-1 rounded border text-xs"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(testQrResult.redeemUrl);
                              toast({ title: t("agency.organizations.testQrCopied") });
                            } catch (_) {
                              toast({ title: t("common.error"), description: t("redeemer.clipboardNoAccess"), variant: "destructive" });
                            }
                          }}
                        >
                          {t("agency.organizations.testQrCopyLink")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded border text-xs"
                disabled={testQrGenerating}
                onClick={() => setTestQrOpen(false)}
              >
                {t("common.close")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {accessLogsLoading && (
          <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
        )}

        <div className="bg-background p-4 rounded shadow space-y-2 text-xs">
          <h3 className="text-sm font-semibold">{t("agency.organizations.newTitle")}</h3>

          <div className="space-y-1">
            <label className="block text-[11px] text-muted-foreground">{t("agency.organizations.name")}</label>
            <input
              type="text"
              className="border px-2 py-1 rounded text-xs w-full"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <label className="block text-[11px] text-muted-foreground">{t("agency.organizations.ownerEmail")}</label>
            <input
              type="email"
              className="border px-2 py-1 rounded text-xs w-full"
              value={newOrgOwnerEmail}
              onChange={(e) => setNewOrgOwnerEmail(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center px-3 py-1.5 rounded bg-blue-600 text-white text-xs disabled:opacity-60"
            disabled={!newOrgName || !newOrgOwnerEmail || creatingOrg}
            onClick={handleCreateOrganization}
          >
            {creatingOrg ? t("agency.organizations.creating") : t("agency.organizations.create")}
          </button>
        </div>

        <div className="bg-background p-4 rounded shadow space-y-2 text-xs">
          <h3 className="text-sm font-semibold">{t("agency.accessLog.title")}</h3>
          <div className="text-[11px] text-muted-foreground">{t("agency.accessLog.desc")}</div>

          {accessLogItemsLoading ? (
            <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
          ) : !accessLogItems?.length ? (
            <div className="text-xs text-muted-foreground">{t("agency.accessLog.empty")}</div>
          ) : (
            <ul className="divide-y divide-border text-xs">
              {accessLogItems.map((l) => (
                <li key={l.id} className="py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">
                        {l.company?.name || "—"}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {t("agency.accessLog.by", {
                          email: l.user?.email || "—",
                          role:
                            l.user?.role === "OWNER"
                              ? t("agency.employees.roleOwner")
                              : t("agency.employees.roleEmployee"),
                        })}
                      </div>
                      {!!l.ip && (
                        <div className="text-[11px] text-muted-foreground truncate">IP: {l.ip}</div>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                      {l.createdAt ? new Date(l.createdAt).toLocaleString("de-DE") : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {accessLogPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                className="text-xs underline disabled:opacity-50"
                disabled={accessLogPage <= 1}
                onClick={() => setAccessLogPage((p) => Math.max(1, p - 1))}
              >
                {t("common.back")}
              </button>

              <div className="text-[11px] text-muted-foreground">
                {t("agency.accessLog.pageLabel", { page: accessLogPage, pages: accessLogPages, total: accessLogTotal })}
              </div>

              <button
                type="button"
                className="text-xs underline disabled:opacity-50"
                disabled={accessLogPage >= accessLogPages}
                onClick={() => setAccessLogPage((p) => Math.min(accessLogPages, p + 1))}
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgencyOrganizationsPage;
